import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { Organization } from '../lib/api';
import { BrandLogo } from '../ui/Brand';
import { Icon } from '../ui/icons';
import { ThemeToggle } from '../theme/ThemeToggle';
import { OrgMembersPanel } from './OrgMembersPanel';

export type DashboardView =
  'boards' | 'calendar' | 'people' | 'groups' | 'homework' | 'myTasks' | 'audit' | 'admin';

export interface OrgSidebarProps {
  orgs: Organization[];
  activeOrgId: string | null;
  view: DashboardView;
  onSelectOrg: (id: string) => void;
  onSelectView: (view: DashboardView) => void;
  onCreateOrg: (name: string) => Promise<void>;
  onRenameOrg: (id: string, name: string) => Promise<void>;
  onDeleteOrg: (id: string) => Promise<void>;
  /** Someone's role changed or they were removed — the roster is stale. */
  onMembersChanged?: () => void;
}

/**
 * Organisation switcher and section nav.
 *
 * Organisations are listed rather than hidden behind a dropdown: with a
 * handful of them, seeing all at once — and their board counts — is faster
 * than opening a menu, and it makes the "boards live inside an organisation"
 * structure obvious without explaining it.
 */
export function OrgSidebar({
  orgs,
  activeOrgId,
  view,
  onSelectOrg,
  onSelectView,
  onCreateOrg,
  onRenameOrg,
  onDeleteOrg,
  onMembersChanged,
}: OrgSidebarProps) {
  const { user, logout } = useAuth();
  const activeOrg = orgs.find((org) => org.id === activeOrgId) ?? null;
  const isOrgAdmin = activeOrg?.myRole === 'admin';
  const isTeacher = activeOrg?.myKind === 'teacher';
  const isCurator = activeOrg?.myKind === 'curator';
  const mayCreateOrg = Boolean(user?.isSuperAdmin || user?.canCreateOrgs);
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitNew = async () => {
    const name = draftName.trim();
    if (name === '') {
      setCreating(false);
      return;
    }
    setBusy(true);
    try {
      await onCreateOrg(name);
      setDraftName('');
      setCreating(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="dash-sidebar">
      <div className="dash-brand">
        <BrandLogo />
        <ThemeToggle />
      </div>

      <nav className="dash-nav">
        <button
          type="button"
          className={`dash-nav-item ${view === 'boards' ? 'is-active' : ''}`}
          aria-current={view === 'boards'}
          onClick={() => onSelectView('boards')}
        >
          <Icon name="boards" size={17} />
          Доски
        </button>
        <button
          type="button"
          className={`dash-nav-item ${view === 'calendar' ? 'is-active' : ''}`}
          aria-current={view === 'calendar'}
          onClick={() => onSelectView('calendar')}
        >
          <Icon name="calendar" size={17} />
          Календарь
        </button>

        {/*
          The tutoring sections are the same material from two sides: teachers
          and the owner keep the people and the homework they set; a student
          sees only what was assigned to them, under the same name.
        */}
        {(isTeacher || isCurator || isOrgAdmin) && (
          <>
            <button
              type="button"
              className={`dash-nav-item ${view === 'people' ? 'is-active' : ''}`}
              aria-current={view === 'people'}
              onClick={() => onSelectView('people')}
            >
              <Icon name="users" size={17} />
              Участники
            </button>
            <button
              type="button"
              className={`dash-nav-item ${view === 'groups' ? 'is-active' : ''}`}
              aria-current={view === 'groups'}
              onClick={() => onSelectView('groups')}
            >
              <Icon name="group" size={17} />
              Группы
            </button>
            <button
              type="button"
              className={`dash-nav-item ${view === 'homework' ? 'is-active' : ''}`}
              aria-current={view === 'homework'}
              onClick={() => onSelectView('homework')}
            >
              <Icon name="clipboard" size={17} />
              Домашняя работа
            </button>
          </>
        )}
        {/*
          Students (and anyone else who is not staff) still need their group:
          classmates, their teacher and the lesson boards live there.
        */}
        {!isTeacher && !isCurator && !isOrgAdmin && (
          <>
            <button
              type="button"
              className={`dash-nav-item ${view === 'groups' ? 'is-active' : ''}`}
              aria-current={view === 'groups'}
              onClick={() => onSelectView('groups')}
            >
              <Icon name="group" size={17} />
              Моя группа
            </button>
            <button
              type="button"
              className={`dash-nav-item ${view === 'myTasks' ? 'is-active' : ''}`}
              aria-current={view === 'myTasks'}
              onClick={() => onSelectView('myTasks')}
            >
              <Icon name="clipboard" size={17} />
              Домашняя работа
            </button>
          </>
        )}
        {isOrgAdmin && (
          <button
            type="button"
            className={`dash-nav-item ${view === 'audit' ? 'is-active' : ''}`}
            aria-current={view === 'audit'}
            onClick={() => onSelectView('audit')}
          >
            <Icon name="history" size={17} />
            Журнал
          </button>
        )}

        {user?.isSuperAdmin && (
          <button
            type="button"
            className={`dash-nav-item ${view === 'admin' ? 'is-active' : ''}`}
            aria-current={view === 'admin'}
            onClick={() => onSelectView('admin')}
          >
            <Icon name="shield" size={17} />
            Админка
          </button>
        )}
      </nav>

      <div className="dash-section-label">Организации</div>

      {/*
        The add button lives inside the list rather than in the section header:
        it belongs to the thing it appends to, and the header is hidden on
        narrow screens where the sidebar becomes a horizontal strip — which
        would otherwise leave no way to create an organisation at all.
      */}
      <ul className="dash-org-list">
        {orgs.map((org) => {
          const active = org.id === activeOrgId;
          if (renamingId === org.id) {
            return (
              <li key={org.id} className="dash-org is-editing">
                <input
                  className="dash-inline-input"
                  defaultValue={org.name}
                  autoFocus
                  maxLength={60}
                  onBlur={async (e) => {
                    const name = e.target.value.trim();
                    setRenamingId(null);
                    if (name && name !== org.name) await onRenameOrg(org.id, name);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      e.currentTarget.value = org.name;
                      e.currentTarget.blur();
                    }
                  }}
                />
              </li>
            );
          }

          return (
            <li key={org.id} className={`dash-org ${active ? 'is-active' : ''}`}>
              <button type="button" className="dash-org-main" onClick={() => onSelectOrg(org.id)}>
                <span className="dash-org-tile" style={{ background: org.color }}>
                  {org.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="dash-org-text">
                  <span className="dash-org-name">{org.name}</span>
                  <span className="dash-org-meta">
                    {org.boardCount ?? 0} досок
                    {(org.onlineUsers ?? 0) > 0 && ` · ${org.onlineUsers} онлайн`}
                  </span>
                </span>
              </button>

              {/* Only an organisation's admin manages it, so a member is not
                  offered buttons the server would refuse. */}
              {(org.myRole === 'admin' || user?.isSuperAdmin) && (
                <span className="dash-org-actions">
                  <OrgMembersPanel
                    orgId={org.id}
                    orgName={org.name}
                    {...(onMembersChanged ? { onChanged: onMembersChanged } : {})}
                  />
                  <button
                    type="button"
                    aria-label={`Переименовать ${org.name}`}
                    data-tip="Переименовать"
                    onClick={() => setRenamingId(org.id)}
                  >
                    <Icon name="pencil" size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Удалить ${org.name}`}
                    data-tip="Удалить"
                    onClick={() => onDeleteOrg(org.id)}
                  >
                    <Icon name="trash" size={13} />
                  </button>
                </span>
              )}
            </li>
          );
        })}

        {/* Creating an organisation: super admins always may, and accounts
            the super admin has granted `canCreateOrgs` may open one they own. */}
        {!mayCreateOrg ? null : creating ? (
          <li className="dash-org is-editing">
            <input
              className="dash-inline-input"
              placeholder="Название организации"
              autoFocus
              maxLength={60}
              disabled={busy}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={submitNew}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitNew();
                if (e.key === 'Escape') setCreating(false);
              }}
            />
          </li>
        ) : (
          <li className="dash-org-add-row">
            <button
              type="button"
              className="dash-org-add"
              aria-label="Создать организацию"
              data-tip="Создать организацию"
              onClick={() => {
                setCreating(true);
                setDraftName('');
              }}
            >
              <span className="dash-org-tile is-ghost">
                <Icon name="plus" size={15} />
              </span>
              <span className="dash-org-add-label">Новая организация</span>
            </button>
          </li>
        )}
      </ul>

      {user && (
        <div className="dash-account">
          <span className="dash-account-avatar" style={{ background: user.color }}>
            {user.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="dash-account-text">
            <span className="dash-account-name">{user.name}</span>
            <span className="dash-account-email">{user.email}</span>
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Выйти"
            data-tip="Выйти"
            onClick={() => void logout()}
          >
            <Icon name="logout" size={15} />
          </button>
        </div>
      )}
    </aside>
  );
}
